import { Option, Result } from "effect";
import * as AsyncData from "foldkit/asyncData";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle, start } from "../../api/asyncData";
import { DELETE_CONFIRMATION, deleteAccount } from "../../api/commands/account";
import { getAiPreferences, setAiPreferences } from "../../api/commands/ai";
import {
	getDigestPreferences,
	setDigestPreferences,
} from "../../api/commands/digest";
import { exportMarkdown, exportUsageData } from "../../api/commands/usage";
import { toMessage } from "../../api/errors";
import type { UsageExport } from "../../api/schemas/usage";
import { i18next } from "../../i18n";
import { Dialog } from "../../widgets/dialog";
import {
	DownloadJson,
	DownloadMarkdown,
	prettyJson,
} from "../../widgets/download";
import type { Page, PageReturn } from "../shared";
import { Message, OutMessage } from "./message";
import type { Model } from "./model";

export const FetchDigest = getDigestPreferences.command(
	Message.CompletedFetchDigest,
);
export const SetDigest = setDigestPreferences.command(
	Message.CompletedSetDigest,
);
export const FetchAi = getAiPreferences.command(Message.CompletedFetchAi);
export const SetAi = setAiPreferences.command(Message.CompletedSetAi);
export const ExportUsage = exportUsageData.command(Message.CompletedExport);
export const ExportMarkdown = exportMarkdown.command(
	Message.CompletedExportMarkdown,
);
/** The API is cross-origin and exposes no Content-Disposition, so the name is fixed here. */
export const MARKDOWN_FILE_NAME = "rhysomn-export.md";
export const DeleteAccount = deleteAccount.command(
	Message.CompletedDeleteAccount,
);

/** v1 `buildExportFileName`: fallback name when the timestamp does not parse. */
export const exportFileName = (exportedAt: string): string => {
	const parsed = new Date(exportedAt);
	return Number.isNaN(parsed.getTime())
		? "rhysomn-usage-export.json"
		: `rhysomn-usage-export-${parsed.toISOString().replace(/[:.]/g, "-")}.json`;
};

type Return = PageReturn<Model, Message, OutMessage>;

const foldDialog = Update.foldChild({
	update: (dialog: Dialog.Model, message: Dialog.Message) =>
		Dialog.update(dialog, message),
	read: (model: Model) => Option.some(model.dialog),
	write: (model: Model, dialog: Dialog.Model) =>
		evo(model, { dialog: () => dialog }),
	toParentMessage: (message: Dialog.Message) =>
		Message.GotDialogMessage({ message }),
	foldOutMessage: () => (model: Model) => ({ model }),
});

const closeDialog = Update.foldChildStep({
	update: Dialog.close,
	read: (model: Model) => Option.some(model.dialog),
	write: (model: Model, dialog: Dialog.Model) =>
		evo(model, { dialog: () => dialog }),
	toParentMessage: (message: Dialog.Message) =>
		Message.GotDialogMessage({ message }),
	foldOutMessage: () => (model: Model) => ({ model }),
});

const openDialog = Update.foldChildStep({
	update: Dialog.open,
	read: (model: Model) => Option.some(model.dialog),
	write: (model: Model, dialog: Dialog.Model) =>
		evo(model, { dialog: () => dialog }),
	toParentMessage: (message: Dialog.Message) =>
		Message.GotDialogMessage({ message }),
	foldOutMessage: () => (model: Model) => ({ model }),
});

const exportSucceeded = (model: Model, payload: UsageExport): Return => ({
	model: evo(model, {
		usageExport: (data) => settle(data, Result.succeed(payload)),
		feedback: () =>
			Option.some({
				kind: "success" as const,
				message: i18next.t("settings.dataExport.exportSuccess", {
					nodeCount: payload.nodes.length,
					knotCount: payload.knots.length,
				}),
			}),
	}),
	commands: [
		DownloadJson({
			fileName: exportFileName(payload.exportedAt),
			json: prettyJson(payload),
		}),
	],
});

export const page: Page<Model, Message, OutMessage> = {
	init: (_route, shared) => ({
		model: {
			page: "Settings",
			digest: AsyncData.Loading(),
			ai: AsyncData.Loading(),
			usageExport: AsyncData.Idle(),
			markdownExport: AsyncData.Idle(),
			feedback: Option.none(),
			dialog: Dialog.init({ id: "confirm_delete_dialog" }),
			deleting: false,
			deleteConfirmation: "",
			deleteError: Option.none(),
		},
		commands: [
			FetchDigest({ generation: shared.generation }),
			FetchAi({ generation: shared.generation }),
		],
	}),
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetchDigest: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: { model: evo(model, { digest: (d) => settle(d, result) }) },
			ToggledDigest: ({ subscribed }) => ({
				model: evo(model, { digest: start }),
				commands: [SetDigest({ subscribed, generation: shared.generation })],
			}),
			// Failure keeps the last known value (Stale) so the toggle springs back.
			CompletedSetDigest: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: { model: evo(model, { digest: (d) => settle(d, result) }) },
			CompletedFetchAi: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: { model: evo(model, { ai: (d) => settle(d, result) }) },
			ToggledAi: ({ enabled }) => ({
				model: evo(model, { ai: start }),
				commands: [SetAi({ enabled, generation: shared.generation })],
			}),
			// Failure keeps the last known value (Stale) so the toggle springs back.
			CompletedSetAi: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: { model: evo(model, { ai: (d) => settle(d, result) }) },
			ClickedThemeToggle: () => ({
				model,
				outMessage: OutMessage.RequestedThemeToggle(),
			}),
			ClickedSystemTheme: () => ({
				model,
				outMessage: OutMessage.RequestedSystemTheme(),
			}),
			ClickedExport: () => ({
				model: evo(model, {
					usageExport: () => AsyncData.Loading(),
					feedback: () => Option.none(),
				}),
				commands: [ExportUsage({ generation: shared.generation })],
			}),
			CompletedExport: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: (payload) => exportSucceeded(model, payload),
							onFailure: (error) => ({
								model: evo(model, {
									usageExport: (data) => settle(data, result),
									feedback: () =>
										Option.some({
											kind: "error" as const,
											message: toMessage(error),
										}),
								}),
							}),
						}),
			ClickedExportMarkdown: () => ({
				model: evo(model, {
					markdownExport: () => AsyncData.Loading(),
					feedback: () => Option.none(),
				}),
				commands: [ExportMarkdown({ generation: shared.generation })],
			}),
			CompletedExportMarkdown: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: {
							model: evo(model, {
								markdownExport: (data) => settle(data, result),
								feedback: () =>
									Option.some(
										Result.match(result, {
											onSuccess: () => ({
												kind: "success" as const,
												message: i18next.t(
													"settings.dataExport.markdownSuccess",
												),
											}),
											onFailure: (error) => ({
												kind: "error" as const,
												message: toMessage(error),
											}),
										}),
									),
							}),
							commands: Result.match(result, {
								onSuccess: (markdown) => [
									DownloadMarkdown({ fileName: MARKDOWN_FILE_NAME, markdown }),
								],
								onFailure: () => [],
							}),
						},
			CompletedDownload: () => ({ model }),
			ChangedDeleteConfirmation: ({ value }) => ({
				model: evo(model, { deleteConfirmation: () => value }),
			}),
			ClickedDelete: () =>
				model.deleteConfirmation === DELETE_CONFIRMATION
					? openDialog(model)
					: { model },
			ClickedConfirmDelete: () =>
				model.deleteConfirmation === DELETE_CONFIRMATION
					? {
							model: evo(model, {
								deleting: () => true,
								deleteError: () => Option.none(),
							}),
							commands: [
								DeleteAccount({
									generation: shared.generation,
									confirmation: model.deleteConfirmation,
								}),
							],
						}
					: { model },
			CompletedDeleteAccount: ({ generation, result }) => {
				if (generation !== shared.generation) {
					return { model };
				}
				const settled = evo(model, { deleting: () => false });
				return Result.match(result, {
					onSuccess: () =>
						Update.withOutMessage(
							closeDialog(settled),
							OutMessage.AccountDeleted(),
						),
					// Failure: the dialog stays open; the banner outside it shows the error.
					onFailure: (error) => ({
						model: evo(settled, {
							deleteError: () => Option.some(toMessage(error)),
						}),
					}),
				});
			},
			GotDialogMessage: ({ message }) => foldDialog(model, message),
		}),
};
